import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { ArrowLeft, MessageCircle, CheckCircle, XCircle, Send, TrendingUp, Award } from 'lucide-react';

export default function BidComparisonPage() {
  const { rfpId } = useParams();
  const navigate = useNavigate();
  const [rfp, setRfp] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBid, setSelectedBid] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalData, setApprovalData] = useState({
    approver_email: '',
    approver_name: '',
    approver_designation: '',
    notes: ''
  });

  useEffect(() => {
    fetchRFPAndBids();
  }, [rfpId]);

  const fetchRFPAndBids = async () => {
    try {
      const [rfpRes, bidsRes] = await Promise.all([
        api.getRFPs(),
        api.getRFPBids(rfpId)
      ]);
      const currentRfp = rfpRes.data.find(r => r.id === rfpId);
      setRfp(currentRfp);
      setBids(bidsRes.data);
    } catch (error) {
      toast.error('Failed to load bids');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      submitted: { color: 'bg-blue-100 text-blue-800', label: 'New Bid' },
      shortlisted: { color: 'bg-purple-100 text-purple-800', label: 'Shortlisted' },
      negotiating: { color: 'bg-yellow-100 text-yellow-800', label: 'Negotiating' },
      pending_approval: { color: 'bg-orange-100 text-orange-800', label: 'Pending Approval' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved ✓' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' }
    };
    const { color, label } = config[status] || config.submitted;
    return <Badge className={color}>{label}</Badge>;
  };

  const handleShortlist = async (bidId) => {
    try {
      await api.updateBidStatus(bidId, 'shortlisted');
      toast.success('Bid shortlisted!');
      fetchRFPAndBids();
    } catch (error) {
      toast.error('Failed to shortlist bid');
    }
  };

  const handleReject = async (bidId) => {
    try {
      await api.updateBidStatus(bidId, 'rejected');
      toast.success('Bid rejected');
      fetchRFPAndBids();
    } catch (error) {
      toast.error('Failed to reject bid');
    }
  };

  const openNegotiation = (bidId) => {
    navigate(`/client/bids/${bidId}/negotiate`);
  };

  const handleSendForApproval = async () => {
    if (!selectedBid) return;
    
    try {
      await api.createApproval({
        bid_id: selectedBid.id,
        ...approvalData
      });
      toast.success('Sent for approval! Your manager will receive an email.');
      setShowApprovalDialog(false);
      fetchRFPAndBids();
    } catch (error) {
      toast.error('Failed to send for approval');
    }
  };

  const lowestBid = bids.length > 0 ? Math.min(...bids.map(b => b.quoted_amount)) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mx-auto mb-4"></div>
          <p className="text-slate-800">Loading bids...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/client/rfps')}
          className="mb-4 sm:mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to RFPs
        </Button>

        {rfp && (
          <Card className="mb-4 sm:mb-6 border-2 border-navy-200">
            <CardHeader className="bg-gradient-to-r from-navy-600 to-navy-800 text-white p-4 sm:p-6">
              <CardTitle className="text-xl sm:text-2xl">{rfp.event_name}</CardTitle>
              <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-navy-100 mt-2">
                <span>🎯 {rfp.event_type}</span>
                <span>📍 {rfp.location}</span>
                <span>👥 {rfp.attendees} attendees</span>
                <span className="hidden sm:inline">📅 {new Date(rfp.start_date).toLocaleDateString()}</span>
              </div>
            </CardHeader>
          </Card>
        )}

        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-navy-800 mb-2">
            Comparing {bids.length} Bids
          </h2>
          {bids.length > 0 && (
            <p className="text-slate-800">
              Lowest bid: ₹{lowestBid.toLocaleString()} | Highest bid: ₹{Math.max(...bids.map(b => b.quoted_amount)).toLocaleString()}
            </p>
          )}
        </div>

        {bids.length === 0 ? (
          <Card className="border-2 border-dashed border-navy-300">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <TrendingUp className="h-16 w-16 text-navy-300 mb-4" />
              <h3 className="text-xl font-semibold text-navy-800 mb-2">
                No Bids Yet
              </h3>
              <p className="text-slate-800 text-center max-w-md">
                Vendors will start submitting bids soon. You'll be notified when new bids arrive.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {bids.map((bid) => (
              <Card key={bid.id} className="border-2 border-navy-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <CardTitle className="text-lg sm:text-xl text-navy-800">
                          {bid.property_name || 'Property'}
                        </CardTitle>
                        {getStatusBadge(bid.status)}
                        {bid.quoted_amount === lowestBid && (
                          <Badge className="bg-gold-100 text-gold-800">
                            <Award className="w-3 h-3 mr-1" />
                            Lowest Bid
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs sm:text-sm text-slate-800">
                        by {bid.vendor_name || 'Vendor'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl sm:text-3xl font-bold text-navy-800">
                        ₹{bid.quoted_amount.toLocaleString()}
                      </div>
                      <div className="text-xs sm:text-sm text-slate-800">Total Quote</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm font-medium text-slate-900 mb-2">✅ Inclusions:</div>
                      <ul className="text-sm text-slate-800 space-y-1">
                        {bid.inclusions?.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900 mb-2">❌ Exclusions:</div>
                      <ul className="text-sm text-slate-800 space-y-1">
                        {bid.exclusions?.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {bid.breakdown && (
                    <div className="mb-4 p-3 bg-navy-50 rounded-lg">
                      <div className="text-sm font-medium text-slate-900 mb-2">Cost Breakdown:</div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-slate-800">
                        {Object.entries(bid.breakdown).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span>{key.replace(/_/g, ' ')}:</span>
                            <span className="font-medium">₹{value.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <div className="text-sm font-medium text-slate-900 mb-1">Payment Terms:</div>
                    <div className="text-sm text-slate-800">{bid.payment_terms}</div>
                  </div>

                  <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                    {bid.status === 'submitted' && (
                      <>
                        <Button
                          onClick={() => handleShortlist(bid.id)}
                          className="flex-1 min-w-[140px] bg-purple-600 hover:bg-purple-700"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Shortlist
                        </Button>
                        <Button
                          onClick={() => handleReject(bid.id)}
                          variant="outline"
                          className="flex-1 min-w-[140px] border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Reject
                        </Button>
                      </>
                    )}

                    {['shortlisted', 'negotiating'].includes(bid.status) && (
                      <>
                        <Button
                          onClick={() => openNegotiation(bid.id)}
                          className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          {bid.status === 'negotiating' ? 'Continue Negotiation' : 'Start Negotiation'}
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedBid(bid);
                            setShowApprovalDialog(true);
                          }}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Send for Approval
                        </Button>
                      </>
                    )}

                    {bid.status === 'pending_approval' && (
                      <div className="flex-1 text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <p className="text-sm text-orange-800 font-medium">
                          ⏳ Waiting for management approval
                        </p>
                      </div>
                    )}

                    {bid.status === 'approved' && (
                      <div className="flex-1 text-center p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-800 font-medium">
                          ✅ Approved! Booking has been created.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 text-xs text-slate-700">
                    Bid submitted: {new Date(bid.created_at).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send for Management Approval</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Approver Name *</label>
              <Input
                required
                placeholder="e.g., John Doe"
                value={approvalData.approver_name}
                onChange={(e) => setApprovalData({...approvalData, approver_name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Approver Email *</label>
              <Input
                required
                type="email"
                placeholder="e.g., ceo@company.com"
                value={approvalData.approver_email}
                onChange={(e) => setApprovalData({...approvalData, approver_email: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Designation *</label>
              <Input
                required
                placeholder="e.g., CEO, CFO, Director"
                value={approvalData.approver_designation}
                onChange={(e) => setApprovalData({...approvalData, approver_designation: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Internal Notes (Optional)</label>
              <Textarea
                rows={3}
                placeholder="Add any internal notes or justification..."
                value={approvalData.notes}
                onChange={(e) => setApprovalData({...approvalData, notes: e.target.value})}
              />
            </div>
            <Button
              onClick={handleSendForApproval}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Send className="mr-2 h-4 w-4" />
              Send Approval Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}